import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Check, Shield, Award, Clock, Users, TrendingUp, Target, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import { testimonials } from '../mock';
import { getServiceData } from '../data/allServicesData';
import useEmblaCarousel from 'embla-carousel-react';
import InquiryModal from '../components/InquiryModal';
import PricingSection from '../components/PricingSection';

const ServiceDetail = () => {
  const { category, serviceSlug } = useParams();
  const content = getServiceData(serviceSlug, category) || {
    category: 'IP Services',
    title: 'Professional IP Services',
    subtitle: 'Expert Legal Protection',
    hero: 'Comprehensive intellectual property protection services.',
    whatIsTitle: 'What is this service?',
    whatIsContent: 'Professional legal services to protect your intellectual property.',
    keyFeatures: [],
    benefits: [],
    pricing: [],
    process: []
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState('');
  const [tmSignUpType, setTmSignUpType] = useState(1);
  const [packageInfo, setPackageInfo] = useState({ title: 'Basic', amount: 45 });

  const handleOpenModal = (context, tmType = null, pkgInfo = null) => {
    setModalContext(context);
    if (tmType !== null) {
      setTmSignUpType(tmType);
    }
    if (pkgInfo !== null) {
      setPackageInfo(pkgInfo);
    }
    setIsModalOpen(true);
  };

  const handleTmSignUpTypeChange = (tmType) => {
    setTmSignUpType(tmType);
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation
  const validatePhone = (phone) => {
    const phoneRegex = /^\+\d{1,3}[\s\-\.]?\(?\d{3}\)?[\s\-\.]?\d{3}[\s\-\.]?\d{4}$/;
    return phoneRegex.test(phone);
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhone(formData.phone)) {
      setErrors(prev => ({ ...prev, phone: 'Please enter a valid phone number with country code (e.g., +1 123-456-7890)' }));
    } else {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate before submission
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
      hasErrors = true;
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number with country code (e.g., +1 123-456-7890)';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiService.createServiceInquiry({
        serviceType: serviceSlug || 'general',
        ...formData
      });
      toast.success(response.message);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setErrors({ email: '', phone: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-[104px]">
      <Helmet>
        <title>{content.title} - {content.subtitle} | Trademark Essential</title>
        <meta name="description" content={`${content.hero.substring(0, 150)}`} />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2B0A12] via-[#3D1C24] to-[#2B0A12] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-[#FFB400] text-sm font-semibold uppercase tracking-wide mb-4">{content.category}</p>
            <h1 className="text-5xl md:text-6xl font-bold text-[#F1F1F1] mb-6">{content.title}</h1>
            <p className="text-2xl text-gray-300 mb-8 max-w-4xl mx-auto">{content.hero}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => {
                  const pricingSection = document.getElementById('pricing');
                  if (pricingSection) {
                    pricingSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-semibold px-8"
              >
                View Pricing
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => handleOpenModal('Free Consultation - Hero')}
                className="border-2 border-[#F1F1F1] text-[#F1F1F1] hover:bg-white hover:text-[#2B0A12]"
              >
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What Is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Content - Left Side */}
            <div className="flex flex-col justify-center">
              <h2 className="text-4xl font-bold text-[#2B0A12] mb-6">{content.whatIsTitle}</h2>
              <h3 className="text-2xl font-semibold text-[#D72638] mb-6">Your Brand's Legal Shield</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {content.whatIsContent}
              </p>
              {content.quote && (
                <div className="bg-gradient-to-r from-[#2B0A12] to-[#3D1C24] p-6 rounded-xl border-l-4 border-[#FFB400]">
                  <p className="text-lg text-gray-200 italic">"{content.quote}"</p>
                </div>
              )}
            </div>

            {/* Image - Right Side - Full Height */}
            <div className="relative h-full min-h-[500px] lg:min-h-0">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-full">
                <img 
                  src={content.whatIsImage || 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070'}
                  alt={content.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2B0A12]/30 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* What This Service Protects */}
          {content.protects && content.protects.length > 0 && (
            <div className="mb-16">
              <h3 className="text-3xl font-bold text-[#2B0A12] text-center mb-10">What a Trademark Protects</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {content.protects.map((item, index) => (
                  <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow">
                    <h4 className="text-xl font-bold text-[#2B0A12] mb-3">{item.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#2B0A12] text-center mb-12">Why Choose Our {content.title}?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.benefits && content.benefits.length > 0 ? content.benefits.map((benefit, index) => {
              const icons = [Shield, Award, Clock, Users, TrendingUp, Target, Zap, Check];
              const IconComponent = icons[index % icons.length];
              return (
                <Card key={index} className="p-6 hover:shadow-xl transition-all hover:-translate-y-1 border-2 border-transparent hover:border-[#FFB400]">
                  <IconComponent className="h-10 w-10 text-[#FFB400] mb-4" />
                  <h3 className="text-lg font-bold text-[#2B0A12] mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
                </Card>
              );
            }) : (
              [Shield, Award, Clock, Users].map((Icon, index) => (
                <Card key={index} className="p-6 hover:shadow-xl transition-shadow">
                  <Icon className="h-12 w-12 text-[#FFB400] mb-4" />
                  <h3 className="text-xl font-bold text-[#2B0A12] mb-2">Benefit {index + 1}</h3>
                  <p className="text-gray-600">Professional service benefit description</p>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Learn More CTA */}
      <section className="py-16 bg-gradient-to-r from-[#2B0A12] to-[#3D1C24]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Learn More about Trademark Protection</h2>
          <p className="text-xl text-gray-300 mb-8">Your brand deserves protection. We're here to help you secure your trademark rights with expert guidance every step of the way.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => handleOpenModal('Register Trademark - Learn More CTA')}
              className="bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-semibold px-8"
            >
              Register Trademark
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => handleOpenModal('Free Consultation - Learn More CTA')}
              className="border-2 border-white text-white hover:bg-white hover:text-[#2B0A12]"
            >
              Free Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      {/* {content.process && content.process.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#2B0A12] mb-4">Simple 3-Step Process</h2>
              <p className="text-lg text-gray-600">Our streamlined process makes {content.title.toLowerCase()} easy and efficient.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {content.process.map((step, index) => (
                <div key={index} className="relative text-center">
                  {index < content.process.length - 1 && (
                    <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#FFB400] to-[#D72638]"></div>
                  )}
                  <div className="relative z-10 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#FFB400] to-[#D72638] text-white text-2xl font-bold mb-6 shadow-lg">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-bold text-[#2B0A12] mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )} */}

      {/* Pricing Section */}
      {serviceSlug === 'search-registration' ? (
        <PricingSection onOpenModal={handleOpenModal} onTmSignUpTypeChange={handleTmSignUpTypeChange} />
      ) : (
        <section className="py-20 bg-white" id="pricing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#2B0A12] mb-4">{content.title} Pricing</h2>
              <p className="text-lg text-gray-600">Transparent pricing with no hidden fees. Choose the package that fits your needs.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto">
              {(content.pricing && content.pricing.length > 0 ? content.pricing : [
                { name: 'Basic', price: 299, features: ['Service Feature 1', 'Service Feature 2', 'Service Feature 3', 'Email Support'] },
                { name: 'Professional', price: 599, popular: true, features: ['Everything in Basic', 'Feature 4', 'Feature 5', 'Phone Support'] },
                { name: 'Enterprise', price: 'Custom', features: ['Everything in Professional', 'Premium Feature', 'Dedicated Manager', '24/7 Support'] }
              ]).map((plan, index) => (
                <Card key={index} className={`w-full md:w-[calc(50%-12px)] lg:w-[280px] p-6 flex flex-col ${plan.popular ? 'border-4 border-[#D72638] shadow-2xl' : 'border-2'}`}>
                  {plan.popular && (
                    <div className="bg-[#D72638] text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
                  )}
                  <h3 className="text-2xl font-bold text-[#2B0A12] mb-2">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-[#D72638]">{typeof plan.price === 'number' ? `$${plan.price}` : plan.price}</span>
                    {plan.description && <span className="text-gray-600 text-sm"> {plan.description}</span>}
                  </div>
                  {plan.subtext && (
                    <p className="text-gray-600 text-sm mb-6">{plan.subtext}</p>
                  )}
                  
                  {/* Scrollable Features List */}
                  <div className="flex-1 overflow-y-auto max-h-64 mb-6 pr-2 scrollbar-thin scrollbar-thumb-[#D72638] scrollbar-track-gray-100">
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <Check className="h-5 w-5 text-[#FFB400] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Button at bottom */}
                  <Button 
                    onClick={() => handleOpenModal(plan.price === 'Custom' ? 'Custom Quote Request' : `Get Started - ${plan.name} Plan`)}
                    className="w-full bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-semibold mt-auto"
                  >
                    {plan.price === 'Custom' ? 'Request Quote' : 'Get Started'}
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comparison Table - Trademark vs Copyright (Only on first trademark and copyright services) */}
      {(serviceSlug === 'search-registration' || serviceSlug === 'registration') && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#2B0A12] mb-4">Trademark vs. Copyright: What's the Difference?</h2>
              <p className="text-lg text-gray-600">Understanding the distinction helps you choose the right protection for your intellectual property.</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <thead className="bg-gradient-to-r from-[#2B0A12] to-[#3D1C24]">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-bold">Aspect</th>
                    <th className="px-6 py-4 text-left text-white font-bold">Trademark</th>
                    <th className="px-6 py-4 text-left text-white font-bold">Copyright</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold text-gray-700">Protects</td>
                    <td className="px-6 py-4 text-gray-600">Brand names, logos, slogans</td>
                    <td className="px-6 py-4 text-gray-600">Original creative works (books, music, art)</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-700">Purpose</td>
                    <td className="px-6 py-4 text-gray-600">Identifies source of goods/services</td>
                    <td className="px-6 py-4 text-gray-600">Protects creative expression</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold text-gray-700">Duration</td>
                    <td className="px-6 py-4 text-gray-600">10 years (renewable indefinitely)</td>
                    <td className="px-6 py-4 text-gray-600">Life of author + 70 years</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-700">Registration</td>
                    <td className="px-6 py-4 text-gray-600">USPTO (United States Patent and Trademark Office)</td>
                    <td className="px-6 py-4 text-gray-600">U.S. Copyright Office</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold text-gray-700">Symbol</td>
                    <td className="px-6 py-4 text-gray-600">® (registered) or ™ (unregistered)</td>
                    <td className="px-6 py-4 text-gray-600">© (copyright)</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-700">Use Required</td>
                    <td className="px-6 py-4 text-gray-600">Yes, must use in commerce</td>
                    <td className="px-6 py-4 text-gray-600">No, automatic upon creation</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Slider */}
      <TestimonialsSlider />

      {/* CTA Section with Background Image */}
      <section 
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2B0A12]/95 via-[#3D1C24]/90 to-[#2B0A12]/95"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Protect Your Rights Today
          </h2>
          <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed">
            Don't wait to secure your intellectual property. Get expert legal assistance from experienced attorneys and safeguard your business future.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <Button 
              size="lg"
              onClick={() => handleOpenModal('Get Started Now - Final CTA')}
              className="bg-[#FFB400] hover:bg-[#e5a200] text-[#2B0A12] font-bold text-lg px-10 py-7 rounded-lg shadow-2xl hover:shadow-[#FFB400]/50 transition-all duration-300 hover:scale-105"
            >
              Get Started Now
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => handleOpenModal('Schedule Consultation - Final CTA')}
              className="border-2 border-white bg-white/10 text-white hover:bg-white hover:text-[#2B0A12] font-bold text-lg px-10 py-7 rounded-lg transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            >
              Schedule Consultation
            </Button>
          </div>
          
          {/* Contact Info */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-white">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-[#FFB400] rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-[#2B0A12]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-300">Call Us</p>
                <p className="text-lg font-bold">+1 (888) 203-1297</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-[#FFB400] rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-[#2B0A12]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-300">Email Us</p>
                <p className="text-lg font-bold">info@trademarkessential.com</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Modal */}
      <InquiryModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        buttonContext={modalContext}
        tmSignUpType={tmSignUpType}
        packageInfo={packageInfo}
      />
    </div>
  );
};

// Testimonials Slider Component
const TestimonialsSlider = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    slidesToScroll: 1
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-[#2B0A12] text-center mb-12">What Our Clients Say</h2>
        
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-colors"
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6 text-[#2B0A12]" />
          </button>
          
          <button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-100 transition-colors"
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6 text-[#2B0A12]" />
          </button>

          {/* Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="flex-[0_0_100%] md:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)] min-w-0">
                  <Card className="p-6 h-full">
                    <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.content}"</p>
                    <div className="flex items-center space-x-4 mt-auto">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name} 
                        className="w-14 h-14 rounded-full object-cover border-2 border-[#FFB400]" 
                      />
                      <div>
                        <h4 className="font-semibold text-[#2B0A12]">{testimonial.name}</h4>
                        <p className="text-sm text-gray-600">{testimonial.role}</p>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceDetail;
